import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Radio, Users, GraduationCap, AlertTriangle, BookOpen, RefreshCw, MapPin, ScanLine } from "lucide-react";
import { toast } from "sonner";

const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—";

const ROLE_LABELS = {
  super_admin: "Direction Système", founder: "Fondateur", director: "Directeur",
  admin: "Administration", finance: "Finance", scolarite: "Scolarité",
  teacher: "Enseignant(e)", nurse: "Infirmerie", canteen: "Cantine", staff: "Personnel",
};

// Pastille de confirmation GPS du scan (position de l'appareil vs périmètre école)
const GpsChip = ({ v, dist }) => {
  if (v === true) return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700" title={dist != null ? `À ${Math.round(dist)} m de l'école` : "Position confirmée"} data-testid="gps-chip-ok">
      <MapPin className="h-2.5 w-2.5" /> GPS ✓
    </span>
  );
  if (v === false) return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700" title={dist != null ? `Scan à ${Math.round(dist)} m de l'école !` : "Hors périmètre"} data-testid="gps-chip-out">
      <MapPin className="h-2.5 w-2.5" /> Hors zone
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-400" title="Position non transmise par l'appareil de scan" data-testid="gps-chip-na">
      <MapPin className="h-2.5 w-2.5" /> GPS —
    </span>
  );
};

// Tableau de présence en temps réel — Admin / Fondateur / Direction / Scolarité
export default function PresenceLivePage() {
  const [data, setData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/presence/live");
      setData(data);
      setLastRefresh(new Date());
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de chargement"); }
  };
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

  if (!data) return <div className="text-slate-400 p-10 text-center">Chargement…</div>;

  const cards = [
    [GraduationCap, "Élèves présents", data.students.present_total, "text-green-700", "presence-students"],
    [Users, "Personnel pointé", data.staff.present_total, "text-[#1E3A8A]", "presence-staff"],
    [BookOpen, "Cours en cours", data.courses.active_total, "text-blue-600", "presence-courses"],
    [AlertTriangle, "Alertes sorties", data.students.alerts, data.students.alerts > 0 ? "text-red-600" : "text-slate-400", "presence-alerts"],
  ];

  return (
    <div className="space-y-6" data-testid="presence-live-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Radio className="h-3 w-3" /><span>TEMPS RÉEL</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Présence dans l&apos;école</h1>
          <p className="text-slate-500 mt-1">Alimenté par le portail (cartes élèves) et le pointage du personnel. Actualisation automatique toutes les 30 s.</p>
        </div>
        <button onClick={load} className="text-sm text-[#1E3A8A] font-semibold flex items-center gap-1 hover:underline" data-testid="presence-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5" />
          {lastRefresh ? `Actualisé à ${lastRefresh.toLocaleTimeString("fr-FR")}` : "Actualiser"}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(([Icon, label, val, cls, tid]) => (
          <div key={label} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={tid}>
            <div className="text-xs uppercase text-slate-500 mb-1 flex items-center gap-1"><Icon className="h-3 w-3" />{label}</div>
            <div className={`text-4xl font-extrabold tabular-nums ${cls}`}>{val}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Élèves par classe */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">Effectif présent par classe</div>
          <div className="p-4">
            {data.students.by_class.length === 0 ? (
              <div className="text-center text-slate-400 py-8 text-sm">Aucun scan au portail aujourd&apos;hui.</div>
            ) : (
              <div className="space-y-2">
                {data.students.by_class.map(c => {
                  const pct = c.total ? Math.round((c.present / c.total) * 100) : null;
                  return (
                    <div key={c.class_name} className="flex items-center gap-3" data-testid={`class-presence-${c.class_name}`}>
                      <div className="w-36 text-sm font-semibold text-slate-700 truncate">{c.class_name}</div>
                      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#166534] rounded-full" style={{ width: `${pct ?? (c.present > 0 ? 100 : 0)}%` }} />
                      </div>
                      <div className="w-20 text-right text-sm tabular-nums font-bold text-[#1E3A8A]">
                        {c.present}{c.total ? `/${c.total}` : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Personnel présent */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">Personnel présent (pointage ouvert)</div>
          <div className="divide-y divide-slate-100 max-h-[320px] overflow-y-auto">
            {data.staff.list.length === 0 && <div className="text-center text-slate-400 py-8 text-sm">Aucun pointage ouvert.</div>}
            {data.staff.list.map(s => (
              <div key={s.employee_id} className="px-5 py-2.5 flex items-center justify-between" data-testid={`staff-present-${s.employee_id}`}>
                <div>
                  <div className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                    {s.name}
                    <GpsChip v={s.gps_verified} dist={s.gps_distance_m} />
                  </div>
                  <div className="text-xs text-slate-500">
                    {ROLE_LABELS[s.role] || s.role}
                    {s.method === "badge" && <span className="ml-1.5 text-[10px] font-bold text-[#1E3A8A]">· badge scanné</span>}
                  </div>
                </div>
                <div className="text-xs font-mono text-green-700 font-bold">depuis {fmtTime(s.since)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Élèves présents — scannés au portail (nominatif, avec confirmation GPS) */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A] flex items-center gap-2">
          <ScanLine className="h-4 w-4" /> Élèves présents — badges scannés au portail
        </div>
        <div className="divide-y divide-slate-100 max-h-[360px] overflow-y-auto" data-testid="students-present-list">
          {data.students.list.length === 0 && <div className="text-center text-slate-400 py-8 text-sm">Aucun élève scanné à l&apos;entrée aujourd&apos;hui.</div>}
          {data.students.list.map((s, i) => (
            <div key={i} className="px-5 py-2.5 flex items-center justify-between" data-testid={`student-present-${s.matricule || i}`}>
              <div>
                <div className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                  {s.student_name}
                  <GpsChip v={s.gps_verified} dist={s.gps_distance_m} />
                </div>
                <div className="text-xs text-slate-500">{s.class_name}{s.matricule ? ` · ${s.matricule}` : ""}</div>
              </div>
              <div className="text-xs font-mono text-green-700 font-bold">entré(e) à {fmtTime(s.since)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Cours en cours */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">Cours en cours (pointage par séance)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Enseignant</th>
                <th className="px-4 py-2 text-left">Classe</th>
                <th className="px-4 py-2 text-left">Matière</th>
                <th className="px-4 py-2 text-left">Salle</th>
                <th className="px-4 py-2 text-left">Depuis</th>
                <th className="px-4 py-2 text-left">Fin prévue</th>
              </tr>
            </thead>
            <tbody>
              {data.courses.list.map((c, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-semibold">{c.teacher_name}</td>
                  <td className="px-4 py-2">{c.class_name}</td>
                  <td className="px-4 py-2">{c.subject}</td>
                  <td className="px-4 py-2 text-slate-500">{c.room || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs text-green-700 font-bold">{fmtTime(c.since)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{c.planned_end}</td>
                </tr>
              ))}
              {data.courses.list.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Aucun cours en cours actuellement.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
